import React, { useEffect, useMemo, useState } from "react";
import Error from "../utils/Error";
import { formatDisplayDate } from "../utils/fomatDate";
import { useEvents } from "../../hooks/useEvent";

const EVENTS_COUNT_KEY = "ss-events-count";
const COLD_START_COUNT = 3;

const IconCalendar = () => (
  <svg
    className="text-sky-blue shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
    <path d="M16 3v4" />
    <path d="M8 3v4" />
    <path d="M4 11h16" />
  </svg>
);

const IconClock = () => (
  <svg
    className="text-sky-blue shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
    <path d="M12 12l3 2" />
    <path d="M12 7v5" />
  </svg>
);

const IconPin = () => (
  <svg
    className="text-sky-blue shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
    <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
  </svg>
);

const isUpcoming = (dateValue) => {
  if (!dateValue) return false;
  const eventDay = new Date(dateValue);
  if (Number.isNaN(eventDay.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDay >= today;
};

const readStoredCount = () => {
  try {
    const n = Number(sessionStorage.getItem(EVENTS_COUNT_KEY));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const writeStoredCount = (count) => {
  try {
    sessionStorage.setItem(EVENTS_COUNT_KEY, String(count));
  } catch {
    /* ignore quota / private mode */
  }
};

const SECTION =
  "w-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-8 sm:py-10 pb-16 sm:pb-20";
const GRID =
  "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 items-stretch w-full";
const CARD_SHELL =
  "w-full bg-surface-white rounded-brand border border-hairline overflow-hidden flex flex-col h-full shadow-brand-sm";
const CARD_IMAGE =
  "relative h-52 sm:h-60 w-full overflow-hidden bg-cloud-mist shrink-0";
const CARD_BODY = "p-5 sm:p-6 flex flex-col flex-1 min-h-0 text-left";
const CARD_TITLE =
  "font-headline text-lg sm:text-xl font-extrabold text-spectrum-blue leading-snug mb-3 line-clamp-2 min-h-[2.75rem] sm:min-h-[3.25rem]";
const CARD_DESC =
  "font-body text-sm text-muted leading-relaxed mb-6 line-clamp-4 min-h-[5.75rem]";
const CARD_META =
  "space-y-3 pt-4 border-t border-hairline mt-auto min-h-[6.5rem]";

/** Same shell + slots as EventCard — equal size, no layout jump. */
const EventCardSkeleton = () => (
  <div className={`${CARD_SHELL} animate-pulse`} aria-hidden="true">
    <div className={CARD_IMAGE}>
      <div className="absolute top-3 right-3 h-6 w-24 rounded-full bg-white-chip" />
    </div>
    <div className={CARD_BODY}>
      <div className={CARD_TITLE}>
        <div className="h-6 sm:h-7 w-4/5 rounded-brand bg-cloud-mist" />
      </div>
      <div className={`${CARD_DESC} space-y-2`}>
        <div className="h-3.5 w-full rounded-brand bg-cloud-mist" />
        <div className="h-3.5 w-full rounded-brand bg-cloud-mist" />
        <div className="h-3.5 w-11/12 rounded-brand bg-cloud-mist" />
        <div className="h-3.5 w-3/4 rounded-brand bg-cloud-mist" />
      </div>
      <div className={CARD_META}>
        <div className="flex items-center gap-3 min-h-5">
          <div className="h-5 w-5 rounded bg-cloud-mist shrink-0" />
          <div className="h-4 w-28 rounded-brand bg-cloud-mist" />
        </div>
        <div className="flex items-center gap-3 min-h-5">
          <div className="h-5 w-5 rounded bg-cloud-mist shrink-0" />
          <div className="h-4 w-36 rounded-brand bg-cloud-mist" />
        </div>
        <div className="flex items-start gap-3 min-h-5">
          <div className="h-5 w-5 rounded bg-cloud-mist shrink-0" />
          <div className="h-4 w-40 rounded-brand bg-cloud-mist" />
        </div>
      </div>
    </div>
  </div>
);

const EventCard = ({ item }) => {
  const upcoming = isUpcoming(item.date);

  return (
    <article
      className={`${CARD_SHELL} transition-all duration-200 hover:-translate-y-1 hover:shadow-brand-md`}
    >
      <div className={CARD_IMAGE}>
        <img
          src={item.img}
          alt={item.title || "Event"}
          className="w-full h-full object-cover"
        />
        <div
          className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-headline font-semibold shadow-sm ${
            upcoming
              ? "bg-spectrum-blue text-surface-white"
              : "bg-cloud-mist text-spectrum-blue"
          }`}
        >
          {upcoming ? "Upcoming" : "Past Event"}
        </div>
      </div>

      <div className={CARD_BODY}>
        <h2 className={CARD_TITLE}>{item.title}</h2>
        <p className={CARD_DESC}>{item.description || "\u00A0"}</p>

        <div className={CARD_META}>
          {item.date ? (
            <div className="flex items-center gap-3 text-ink min-h-5">
              <IconCalendar />
              <span className="font-headline text-sm font-medium">
                {formatDisplayDate(item.date)}
              </span>
            </div>
          ) : null}
          {item.time ? (
            <div className="flex items-center gap-3 text-ink min-h-5">
              <IconClock />
              <span className="font-headline text-sm font-medium">{item.time}</span>
            </div>
          ) : null}
          {item.location ? (
            <div className="flex items-start gap-3 text-ink min-h-5">
              <IconPin />
              <span className="font-headline text-sm font-medium">
                {item.location}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

/**
 * Event cards from GET /api/website/marketing-events — Stitch card layout.
 * Skeleton count tracks last known event count so grid size does not jump.
 */
const AllEvents = () => {
  const {
    data: events,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useEvents();
  const [storedCount, setStoredCount] = useState(readStoredCount);

  const list = useMemo(
    () =>
      (events?.data || [])
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [events]
  );

  useEffect(() => {
    if (!list.length) return;
    writeStoredCount(list.length);
    setStoredCount(list.length);
  }, [list.length]);

  // Last known count keeps the grid the same size on return visits;
  // cold start falls back to one lg row, which is the shape it resolves to.
  const skeletonCount = list.length || storedCount || COLD_START_COUNT;
  // Skeletons only while there is nothing to show; refetch keeps previous cards (no jump).
  const showSkeletons = isLoading && list.length === 0;

  if (isError && !list.length) {
    return (
      <div className={SECTION}>
        <Error
          title="Upcoming events aren’t loading"
          message={error.message}
          onRetry={refetch}
        />
      </div>
    );
  }

  return (
    <div
      className={SECTION}
      aria-busy={showSkeletons || isFetching}
      aria-live="polite"
    >
      {showSkeletons && <p className="sr-only">Loading events</p>}

      {showSkeletons ? (
        <div className={GRID}>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <EventCardSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      ) : !list.length ? (
        <div className="text-left">
          <div className="bg-surface-white border border-hairline rounded-brand p-8 shadow-brand-sm max-w-2xl">
            <h2 className="font-headline font-extrabold text-xl text-spectrum-blue mb-3">
              Stay tuned for exciting opportunities
            </h2>
            <p className="font-body text-muted leading-relaxed">
              At Social Spectrum, we believe in the power of connection and shared
              experiences. Our upcoming events bring families together and provide
              resources for navigating the spectrum. Check back soon!
            </p>
          </div>
        </div>
      ) : (
        <div className={GRID}>
          {list.map((item) => (
            <EventCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AllEvents;
